import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

// components
import { ListBox } from 'primereact/listbox'

import OverlayPanel from '@aytm/app/stats/components/overlay-panel/OverlayPanel'
import { Tooltip } from '@aytm/app/stats/components/tooltip'

// styles
import style from '@aytm/app/stats/components/export-dropdown/ExportDropdown.module.scss'

const MIN_SPINNER_MS = 250

// The page runs one download at a time and refuses another meanwhile; the menu says so for a moment
// instead of spinning on a request that was never started.
export const EXPORT_BUSY_MESSAGE =
  'A download is already in progress. Please wait for it to complete before starting another.'
export const EXPORT_BUSY_NOTICE_MS = 4000

// each entry is addressable by its export key, whatever its label reads
const optionTemplate = option => <span data-testid={`stat-export-dropdown-option-${option.key}`}>{option.text}</span>

const ExportDropdown = ({ className, text = '', tooltip = 'Export', options, onExport, disabled = false }) => {
  const [open, setOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [busyNotice, setBusyNotice] = useState(false)

  const ref = useRef(null)
  const componentRef = useRef(null)

  useEffect(() => {
    if (!busyNotice) return
    const timer = setTimeout(() => setBusyNotice(false), EXPORT_BUSY_NOTICE_MS)
    return () => clearTimeout(timer)
  }, [busyNotice])

  const onExportClick = async event => {
    ref.current?.hide()

    // the spinner branch unmounts the panel before its own onHide fires, so the trigger would
    // stay painted active once it comes back
    setOpen(false)
    setExporting(true)
    setBusyNotice(false)

    // originalEvent is passed on so handlers can read modifier keys (shift bypasses the export cache)
    await onExport(
      event.value,
      (result = {}) =>
        setTimeout(() => {
          setExporting(false)
          if (result?.busy) setBusyNotice(true)
        }, MIN_SPINNER_MS),
      event.originalEvent
    )
  }

  return 0 < options.length ? (
    <div
      ref={componentRef}
      className={classNames(className, style.export, { [style.exporting]: exporting, [style.disabled]: disabled })}
    >
      {0 < text.length ? <span className={style['export-text']}>{text}</span> : null}
      {disabled ? (
        <span className={style['export-icon']} title={tooltip}>
          <i className="icon-aytmfd-menu-v2" />
        </span>
      ) : exporting ? (
        <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
      ) : (
        <>
          <OverlayPanel
            ref={ref}
            testId="stat-export-dropdown-button"
            panelClassName={style.panel}
            button={
              <span className={classNames(style['export-icon'], { [style.active]: open })} title={tooltip}>
                <i className="icon-aytmfd-menu-v2" />
              </span>
            }
            onShow={() => setOpen(true)}
            onHide={() => setOpen(false)}
          >
            <ListBox
              className="p-listbox-no-scroll"
              options={options}
              optionLabel="text"
              optionValue="key"
              itemTemplate={optionTemplate}
              onChange={onExportClick}
            />
          </OverlayPanel>
        </>
      )}
      {busyNotice ? <Tooltip text={EXPORT_BUSY_MESSAGE} target={componentRef.current} wrap /> : null}
    </div>
  ) : null
}

ExportDropdown.propTypes = {
  className: PropTypes.string,
  text: PropTypes.string,
  tooltip: PropTypes.string,
  options: PropTypes.array,
  onExport: PropTypes.func,
  disabled: PropTypes.bool
}

export default ExportDropdown
